/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { defineStore } from "pinia";
import { ref } from "vue";
import { weightRepository } from "@/services/repositories/weight.repository.js";

export const useWeightStore = defineStore("weight", () => {
  const weights = ref([]);
  const allWeights = ref([]);
  const loading = ref(false);
  const error = ref(null);

  async function fetchWeights(goatId) {
    loading.value = true;
    error.value = null;
    try {
      const id = Number(goatId);
      if (isNaN(id)) throw new Error("Undefined Goat ID.");
      weights.value = await weightRepository.findByGoat(id);
    } catch (e) {
      error.value = e.message;
      weights.value = [];
    } finally {
      loading.value = false;
    }
  }

  //fetch all weight data for activities view
  async function fetchAllWeights() {
    loading.value = true;
    error.value = null;
    try {
      allWeights.value = await weightRepository.findAll();
      console.log(`Fetched ${allWeights.value.length} weights`);
    } catch (e) {
      error.value = e.message;
      allWeights.value = [];
    } finally {
      loading.value = false;
    }
  }

  async function addWeight(goatId, value, measuredAt = null) {
    loading.value = true;
    error.value = null;
    try {
      const id = Number(goatId);
      if (isNaN(id)) throw new Error("Undefined Goat ID.");
      await weightRepository.create({ goatId: id, value, measuredAt });
      await fetchWeights(id); // refresh
    } catch (e) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  async function updateWeight(id, goatId, value, measuredAt = null) {
    loading.value = true;
    error.value = null;
    try {
      const gId = Number(goatId);
      if (isNaN(gId)) throw new Error("Undefined Goat ID.");
      await weightRepository.update(id, { value, measuredAt });
      await fetchWeights(gId);
    } catch (e) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  async function deleteWeight(id, goatId) {
    loading.value = true;
    error.value = null;
    try {
      const gId = Number(goatId);
      if (isNaN(gId)) throw new Error("Undefined Goat ID.");
      await weightRepository.delete(id);
      await fetchWeights(gId);
    } catch (e) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  async function getLatestWeight(goatId) {
    loading.value = true;
    error.value = null;
    try {
      const id = Number(goatId);
      if (isNaN(id)) throw new Error("Undefined Goat ID.");
      return await weightRepository.latest(id);
    } catch (e) {
      error.value = e.message;
      return null;
    } finally {
      loading.value = false;
    }
  }

  return {
    weights,
    allWeights,
    loading,
    error,
    fetchWeights,
    fetchAllWeights,
    addWeight,
    updateWeight,
    deleteWeight,
    getLatestWeight
  };
});
